import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { formatPingAge } from '../utils/pingAge';
import type { OpenPingFeed, OpenPingFeedItem } from '../types/repairOrders';

/**
 * Persistent toasts for outstanding maintenance requests, driven by the feed
 * `useOpenPingFeed` already polls (this hook never fetches).
 *
 * WHY THIS EXISTS: the `Requests` column on the fleet Repair Orders page ships
 * HIDDEN by default so nobody's saved layout shifts, which means a request can
 * sit unseen indefinitely. A ping nobody sees is worse than the email it
 * replaced.
 *
 * WHY THE CAP: maintenance coming back from time off would otherwise face
 * fifteen stacked toasts and dismiss the lot unread — which is the exact
 * failure this feature exists to prevent. At most three individual toasts,
 * then ONE collapsed "N more requests waiting" that clicks through to the
 * filtered page.
 */
export const MAX_INDIVIDUAL_PING_TOASTS = 3;

/** The Repair Orders page reads `?requests=open` and applies its open-request filter. */
export const REPAIR_ORDERS_OPEN_REQUESTS_PATH = '/fleet-management/repair-orders?requests=open';

export const OPEN_PING_DISMISS_STORAGE_KEY = 'repairOrderPingDismissals';

const PING_LABELS: Record<string, string> = {
  eta: 'ETA requested',
  completion: 'Completion requested',
};

/**
 * One line naming the request. `pronumber` is nullable (denormalized column,
 * no FK), so fall back to the RO rather than rendering "PRO null".
 */
export const describePing = (p: OpenPingFeedItem, now?: Date): string =>
  [
    PING_LABELS[p.requestType] || 'Request',
    p.pronumber != null ? `PRO ${p.pronumber}` : `RO ${p.roRecno}`,
    p.requestedBy,
    // The feature's ONE relative-age helper. requestedAt is NAIVE LOCAL time;
    // see its doc comment for why it must not be treated as UTC.
    formatPingAge(p.requestedAt, now),
  ]
    .filter(Boolean)
    .join(' · ');

// EVERY localStorage touch is wrapped: private windows, cleared site data and
// quota errors all throw or return null, and none of them may break the nav.
const readDismissed = (): number[] => {
  try {
    const raw = window.localStorage.getItem(OPEN_PING_DISMISS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === 'number') : [];
  } catch {
    return [];
  }
};

const writeDismissed = (ids: number[]): void => {
  try {
    window.localStorage.setItem(OPEN_PING_DISMISS_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable — the toast still closed for this session, it just
    // will not stay closed across a refresh. That is the right degradation.
  }
};

/**
 * @param enabled  The user's `showRepairOrderToast` preference. Off means raise
 *   nothing AND close anything already raised, so switching it off clears the
 *   screen instead of leaving stale toasts until the next reload. The feed
 *   itself keeps polling and the nav badge keeps counting — only the intrusive
 *   half is suppressed. Turning it back on re-raises the still-open,
 *   still-undismissed pings, exactly as a page refresh does: they are
 *   unanswered requests, and the toasts are persistent for that reason.
 */
export function useOpenPingToasts(feed: OpenPingFeed, enabled: boolean = true): void {
  const { showToast, dismissToast } = useToast();
  const navigate = useNavigate();

  /** ping recno -> toast id, for the individual toasts currently raised. */
  const raisedRef = useRef<Map<number, number>>(new Map());
  /** The collapsed "N more" toast and the number it is currently reporting. */
  const collapsedRef = useRef<{ id: number; count: number } | null>(null);
  /** Toast ids WE closed. onDismiss fires for those too, and a programmatic
   *  close (the ping got answered) must not be recorded as a user dismissal. */
  const selfClosedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const closeOurs = (id: number) => {
      // Recorded as OURS: onDismiss fires for a programmatic close too, and
      // silencing the toasts must not be written down as the user dismissing
      // every open request.
      selfClosedRef.current.add(id);
      dismissToast(id);
    };

    if (!enabled) {
      for (const [recno, toastId] of Array.from(raisedRef.current.entries())) {
        closeOurs(toastId);
        raisedRef.current.delete(recno);
      }
      if (collapsedRef.current) {
        closeOurs(collapsedRef.current.id);
        collapsedRef.current = null;
      }
      return;
    }

    const open = feed?.open ?? 0;
    const pings = feed?.pings ?? [];

    // `pings` is capped while `open` is the true total, so past the cap there
    // are open pings NOT in this list. Pruning against the list then would drop
    // a dismissal for a ping that is still open and re-raise it later, so only
    // prune when the feed is complete.
    const capped = open > pings.length;
    let dismissed = readDismissed();
    if (!capped) {
      const stillOpen = new Set(pings.map((p) => p.recno));
      const pruned = dismissed.filter((id) => stillOpen.has(id));
      if (pruned.length !== dismissed.length) {
        dismissed = pruned;
        writeDismissed(pruned);
      }
    }

    const dismissedSet = new Set(dismissed);
    const undismissed = pings.filter((p) => !dismissedSet.has(p.recno));
    const toShow = undismissed.slice(0, MAX_INDIVIDUAL_PING_TOASTS);
    const toShowIds = new Set(toShow.map((p) => p.recno));

    // Close toasts for pings that were answered, dismissed, or pushed under the cap.
    for (const [recno, toastId] of Array.from(raisedRef.current.entries())) {
      if (!toShowIds.has(recno)) {
        closeOurs(toastId);
        raisedRef.current.delete(recno);
      }
    }

    for (const p of toShow) {
      if (raisedRef.current.has(p.recno)) continue;
      const holder: { id?: number } = {};
      const id = showToast({
        type: 'warning',
        persistent: true,
        message: describePing(p),
        onClick: () => navigate(REPAIR_ORDERS_OPEN_REQUESTS_PATH),
        onDismiss: () => {
          if (holder.id !== undefined && selfClosedRef.current.delete(holder.id)) return;
          const next = readDismissed();
          if (!next.includes(p.recno)) next.push(p.recno);
          writeDismissed(next);
        },
      });
      holder.id = id;
      raisedRef.current.set(p.recno, id);
    }

    // Everything the user has not been shown individually: the undismissed rows
    // past the cap, plus the open pings the feed itself truncated away (those
    // are undismissed by definition — they were never delivered).
    const beyondFeedCap = Math.max(0, open - pings.length);
    const remaining = Math.max(0, undismissed.length - MAX_INDIVIDUAL_PING_TOASTS) + beyondFeedCap;

    if (remaining > 0) {
      if (collapsedRef.current && collapsedRef.current.count !== remaining) {
        closeOurs(collapsedRef.current.id);
        collapsedRef.current = null;
      }
      if (!collapsedRef.current) {
        const id = showToast({
          type: 'warning',
          persistent: true,
          message: `${remaining} more request${remaining === 1 ? '' : 's'} waiting`,
          onClick: () => navigate(REPAIR_ORDERS_OPEN_REQUESTS_PATH),
        });
        collapsedRef.current = { id, count: remaining };
      }
    } else if (collapsedRef.current) {
      closeOurs(collapsedRef.current.id);
      collapsedRef.current = null;
    }
  }, [feed, enabled, showToast, dismissToast, navigate]);
}

export default useOpenPingToasts;
